import { useState, useEffect } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import AgencyForm from "@/components/agency-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input"; 
import { supabase } from "../../supabaseClient";

export default function Agencies() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation("/login");
    }
  }, [user, isLoading, setLocation]);

  useEffect(() => {
    const fetchAgencies = async () => {
      const { data, error } = await supabase.from("agency").select("*");

      if (error) {
        console.error("Error fetching agencies:", error.message);
      } else {
        setAgencies(data);
      }
    };

    fetchAgencies();
  }, []);

  const handleSuccess = (newAgency: Agency) => {
    setOpen(false);
    setAgencies((prevAgencies) => [...prevAgencies, newAgency]);
  };

  const filteredAgencies = agencies.filter((agency) =>
    agency?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) return <div>Loading...</div>;
  if (!user) return null;

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Agencies</h2>
            <p className="text-muted-foreground">Manage your agency relationships</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Agency
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Agency</DialogTitle>
              </DialogHeader>
              <AgencyForm onSuccess={handleSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        <Input
          placeholder="Search Agency..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-4"
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredAgencies?.map((agency) => (
            agency ? (
              <Card
                key={agency.id}
                className="relative cursor-pointer"
                onClick={() => {
                  setLocation(`/agency-details/${agency.id}`);
                }}
              >
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{agency.name || "Unknown Agency"}</h3>
                      <Badge
                        variant={agency.commissionPercentage > 0 ? "default" : "secondary"}
                      >
                        {agency.commissionPercentage || 0}%
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Mail className="mr-2 h-4 w-4" />
                        {agency.email || "No email available"}
                      </div>
                      {agency.phone && (
                        <div className="flex items-center">
                          <Phone className="mr-2 h-4 w-4" />
                          {agency.phone}
                        </div>
                      )}
                      {agency.created_at && (
                        <div className="text-xs text-muted-foreground">
                          Created on: {new Date(agency.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null
          ))}
        </div>
      </div>
    </Layout>
  );
}
